/**
 * Storage Service
 * 
 * Centralizes storage operations (localStorage) to provide a consistent API
 * and enable future enhancements like encryption or different storage backends.
 */
import { logger } from './logger.service';

// Storage keys used throughout the application.
// NOTE (RBAC): auth is memory-only access token + httpOnly refresh cookie.
// Legacy localStorage auth entries are purged one-shot via
// `purgeLegacyAuthKeys()` below. New code MUST NOT persist auth in storage.
export enum StorageKey {
  DEVICE_ID = 'device_id',
  THEME = 'ui-theme',
  /** @deprecated Legacy vendor-auth key — new code MUST NOT use; purged by purgeLegacyAuthKeys(). */
  TOKEN = 'token',
  /** @deprecated Legacy vendor-auth key — new code MUST NOT use; purged by purgeLegacyAuthKeys(). */
  REFRESH_TOKEN = 'refresh_token',
  /** @deprecated Legacy vendor-auth key — new code MUST NOT use; purged by purgeLegacyAuthKeys(). */
  USER = 'user',
}

/** Legacy localStorage auth keys purged one-shot on admin boot (RBAC). */
export const LEGACY_AUTH_KEYS = ['token', 'refresh_token', 'user'] as const;

/** One-shot removal of legacy auth keys (safe to call repeatedly). */
export function purgeLegacyAuthKeys(): void {
  for (const key of LEGACY_AUTH_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      logger.error(`Error purging legacy localStorage key "${key}":`, error);
    }
  }
}

class StorageService {
  /**
   * Set a value in storage
   * @param key The key to store the value under
   * @param value The value to store
   */
  set<T>(key: StorageKey | string, value: T): void {
    try {
      const serializedValue = typeof value === 'string' 
        ? value 
        : JSON.stringify(value);
      
      localStorage.setItem(key, serializedValue);
    } catch (error) {
      logger.error(`Error setting localStorage key "${key}":`, error);
    }
  }

  /**
   * Get a value from storage
   * @param key The key to retrieve
   * @param defaultValue The default value to return if the key doesn't exist
   * @returns The stored value, or defaultValue if not found
   */
  get<T>(key: StorageKey | string, defaultValue: T | null = null): T | null {
    try {
      const value = localStorage.getItem(key);
      
      if (value === null) {
        return defaultValue;
      }
      
      // Try to parse as JSON, but return as string if parsing fails
      try {
        return JSON.parse(value) as T;
      } catch {
        return value as unknown as T;
      }
    } catch (error) {
      logger.error(`Error getting localStorage key "${key}":`, error);
      return defaultValue;
    }
  }

  /**
   * Remove a value from storage
   * @param key The key to remove
   */
  remove(key: StorageKey | string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      logger.error(`Error removing localStorage key "${key}":`, error);
    }
  }

  /**
   * Check if a key exists in storage
   * @param key The key to check
   * @returns True if the key exists
   */
  has(key: StorageKey | string): boolean {
    return localStorage.getItem(key) !== null;
  }

  /**
   * Clear all values from storage
   */
  clear(): void {
    try {
      localStorage.clear();
    } catch (error) {
      logger.error('Error clearing localStorage:', error);
    }
  }
}

// Export singleton instance
export const storageService = new StorageService();

// Also export as default for compatibility with JavaScript imports
export default storageService;
